// Beacon presentation helpers — mirrors backend/src/beacon/beacon.scoring.ts for
// the score model, plus the bucket/format helpers ported from the original atlas.js.

export const spendBucket = (v: number): number =>
  v < 2 ? 1 : v < 4 ? 2 : v < 7 ? 3 : v < 12 ? 4 : 5;

// Priority Score buckets — ps = s * pain * (5 - m), range 0..100.
export const psBucket = (ps: number | null | undefined): number => {
  if (ps == null) return 0;
  if (ps <= 0) return 0;
  if (ps <= 20) return 1;
  if (ps <= 40) return 2;
  if (ps <= 60) return 3;
  if (ps <= 80) return 4;
  return 5;
};

export const money = (m: number): string => '$' + (m ?? 0).toFixed(1) + 'M';

// ---- Derived score model (mirrors backend/src/beacon/beacon.scoring.ts) ----
// Kept in sync so the authoring panel can preview target/gap/ps live before save.
//   target = current + 2  if S >= 4 and M <= 2, else current + 1, capped at 5
//   gap    = max(0, target - current)
//   ps     = S * Pain * (5 - Maturity)

export interface ScoreInputs {
  maturity: number;
  pain: number;
  importance: number;
  targetMaturity?: number | null;
}

/** Default target maturity per the model, used when none is explicitly set. */
export const defaultTarget = (maturity: number, importance: number): number => {
  const bump = importance >= 4 && maturity <= 2 ? 2 : 1;
  return Math.min(5, maturity + bump);
};

export const computeTarget = (i: ScoreInputs): number => {
  const t = i.targetMaturity ?? defaultTarget(i.maturity, i.importance);
  return Math.max(i.maturity, Math.min(5, t));
};

export const computeGap = (i: ScoreInputs): number => Math.max(0, computeTarget(i) - i.maturity);

export const computePriorityScore = (i: ScoreInputs): number => i.importance * i.pain * (5 - i.maturity);
